import { KubernetesObject } from '@kubernetes/client-node'
import * as yaml from 'js-yaml'
import * as _ from 'lodash'
import * as path from 'path'
import { ExecuteContext, IApplicationModel, KubernetesOperator, KV, KVString, PlanContext, PluginInfoList } from "yam-types"
import { COPIED_APP_MODEL_FIELD } from '.'
import { DEFAULT_KUBE_CONFIG, YAM_PREFIX } from "../constants"
import TemplateRender from "../template/render"
import { OperatorParam, PlanFileStoreType } from "../types.d"
import { checkApplicationModelPath, createLogger, fsMkdir, fsRead, fsRemove, fsRmdir, fsWrite, generateWorkloadNameWithEnv, lookupWildcardKeyObj, uuid, zipDirectory } from "../util"

export const MANIFEST_FILE = 'manifest.json'
export const OPERATE_PARAM = 'params.json'
export const ACTIONS = 'actions-preview'
export const APPLIED_RESOURCES = 'applied-resources.yaml'
export const SYNC_TIMESTAMP = 'timestamp'
export const FINISHED = 'finished'
export const PLUGIN_INFO_LIST = 'plugin-info-list'

export default class YamStateStore {

  private storeType: PlanFileStoreType
  private operatorParam: OperatorParam

  private log = createLogger("state-store")

  constructor(operatorParam: OperatorParam) {
    this.storeType = operatorParam.engine.clientConf.planStoreType
    this.operatorParam = operatorParam
  }

  async persistPlan(plan: PlanContext, param: OperatorParam, render: TemplateRender): Promise<void> {
    if (plan.data.templateOutput) {
      return
    }
    if (this.operatorParam.runMode !== "plan-only") {
      this.log.info(`run in ${this.operatorParam.runMode} mode, skip persisting plan-file.`)
      return
    }

    const planFile = path.join(param.workingDir || process.cwd(), param.planFile || `release-plan-${Date.now()}.ygz`)
    this.log.info(`start persisting YAM plan file into: ${planFile}, mode: ${this.storeType}`)

    // store all model files and template values
    const tmpDir = uuid()
    try {
      await fsMkdir(tmpDir)
      const manifest = {} as KVString
      for (const [key, value] of render.fileContentCache) {
        const tmpFile = uuid()
        await fsWrite(tmpDir + '/' + tmpFile, value)
        manifest[key] = tmpFile
      }
      await fsWrite(tmpDir + '/' + MANIFEST_FILE, JSON.stringify(manifest))
      const params = plan._.omit(param, "workingDir", "engine", "planFile") as unknown as KVString
      await fsWrite(tmpDir + '/' + OPERATE_PARAM, JSON.stringify(params))
      const actions = plan.data.actions.map(action => (action as unknown as KV).actionName || action.name || '<anonymous>')
      await fsWrite(tmpDir + '/' + ACTIONS, actions.join('\n'))
      await zipDirectory(tmpDir, planFile)
      this.log.info(`plan file generated, planned actions are:\n${actions.join('\n')}`)
    } finally {
      await fsRmdir(tmpDir)
    }
    if (this.storeType === "cloud") {
      // TODO send HTTP request to upload file
      await fsRemove(planFile)
    }
    this.log.info(`release plan stored.`)
  }

  static async loadPlan(planDir: string, param: OperatorParam, render: TemplateRender): Promise<void> {
    const mfFile = await fsRead(planDir + '/' + MANIFEST_FILE)
    const modelFiles = JSON.parse(mfFile.toString())
    for (const key in modelFiles) {
      const buf = await fsRead(planDir + '/' + modelFiles[key])
      render.fileContentCache.set(key, buf.toString())
    }
    const paramsBuf = await fsRead(planDir + '/' + OPERATE_PARAM)
    const paramInPlan = JSON.parse(paramsBuf.toString()) as OperatorParam
    const cmdParamObj = paramInPlan.cmdParams as unknown as KVString
    // merge cmd line parameters
    for (const key in cmdParamObj) {
      param.cmdParams.set(key, cmdParamObj[key])
    }
    param.statusWaitSeconds = paramInPlan.statusWaitSeconds
    param.noDiff = paramInPlan.noDiff
    param.forceRestartWorkload = paramInPlan.forceRestartWorkload

    const ctxMap = param.engine.clientConf.contextPatternMapping
    const kubeFileMap = param.engine.clientConf.kubeConfigFilePatternMapping
    const kubeConf = param.engine.clientConf.kubeConfig || DEFAULT_KUBE_CONFIG()
    paramInPlan.clusters.forEach(c => {
      if (c.rawClusterName) {
        c.kubeConfContextName = lookupWildcardKeyObj(c.rawClusterName, ctxMap) as string || c.rawClusterName
        c.kubeConfPath = lookupWildcardKeyObj(c.rawClusterName, kubeFileMap) as string || kubeConf
      }
    })
    param.clusters = paramInPlan.clusters
  }

  async storeYamMetadata(plan: PlanContext, apply: ExecuteContext, fileCache: Map<string, string>, finished: boolean, error?: Error): Promise<void> {
    if (plan.data.templateOutput) {
      return
    }
    if (this.operatorParam.runMode === "plan-only") {
      return
    }
    this.log.info(`storing operation results.`)
    const yamPath = checkApplicationModelPath(this.operatorParam.workingDir, fileCache)

    if (!error) {
      const confName = YAM_PREFIX + generateWorkloadNameWithEnv(plan.data.app, plan.data.envName)
      try {
        const appliedResStore: KubernetesObject[] = []
        const existing = await apply.k8sClient.getConf({ name: confName, secret: true })

        if (existing && existing.data[APPLIED_RESOURCES]) {
          const deletedRes = apply.k8sClient.deletedK8SResources
          const existingResList = yaml.loadAll(existing.data[APPLIED_RESOURCES]) as KubernetesObject[]
          const existingListFlatten = _.flattenDeep(existingResList)
          existingListFlatten.forEach((res) => {
            const deletedKindRes = deletedRes.get(res.kind || '')
            if (deletedKindRes && deletedKindRes.filter(r => res.metadata?.name === r.metadata?.name && res.metadata?.namespace === r.metadata?.namespace).length > 0) {
              this.log.info(`deleted resource: ${res.kind} - ${res.metadata?.name}`)
            } else {
              appliedResStore.push(res)
            }
          })
        }

        // new applied resources
        const appliedRes = apply.k8sClient.appliedK8SResources
        appliedRes.forEach((values, kind) => {
          values.forEach(obj => {
            if (appliedResStore.filter(r => r.kind === obj.kind && r.metadata?.name === obj.metadata?.name && r.metadata?.namespace === obj.metadata?.namespace).length > 0) {
              this.log.debug(`skip adding resource: ${obj.kind} - ${obj.metadata?.name} into operation result, already recorded last time`)
              return
            }
            appliedResStore.push({
              kind,
              metadata: {
                name: obj.metadata?.name,
                namespace: obj.metadata?.namespace
              }
            })
          })
        })
        await apply.k8sClient.saveConf({
          name: confName,
          data: new YamMetadata(
            appliedResStore,
            new Date(),
            finished,
            yamPath,
            (plan.data as KV)[COPIED_APP_MODEL_FIELD] as IApplicationModel || plan.data.currentModelFull,
            plan.data.pluginInfoList
          ).toKV(),
          secret: true
        })
        this.log.info(`operation results persisted.`)
      } catch (ex) {
        this.log.error(`can not store operation results: ${ex.message}`)
        if (this.log.isLevelEnabled('debug')) {
          this.log.error(ex)
        }
        return
      }
    }
    // store YAM as secret for rollback, store managedResources in case of uninstall
    // TODO in cloud mode, each plan/apply has unique id, result could send to cloud with that id, for cloud dashboards
  }

  static async loadYamMetadata(
    workloadName: string,
    // All possible yamPaths can be pass when yamPath is uncertain
    yamPath: string | string[],
    confGetter: {
      getConf: KubernetesOperator['getConf']
    }): Promise<YamMetadata | null> {

    const confName = YAM_PREFIX + workloadName
    const metadata = await confGetter.getConf({ name: confName, secret: true })
    if (!metadata) {
      return null
    }
    if (typeof yamPath === 'string') {
      return YamMetadata.fromKV(metadata.data, yamPath)
    }

    const p = yamPath.find(p => p in metadata.data)
    if (!p) {
      return null
    }
    return YamMetadata.fromKV(metadata.data, p)
  }
}

export class YamMetadata {

  constructor(
    public appliedResources: KubernetesObject[],
    public timestamp: Date,
    public finished: boolean,
    public yamPath: string,
    public appModel: IApplicationModel,
    public pluginInfoList: PluginInfoList
  ) { }

  static fromKV(kv: KVString, yamPath: string): YamMetadata {
    return new YamMetadata(
      yaml.loadAll(kv[APPLIED_RESOURCES]) as KubernetesObject[],
      new Date(kv[SYNC_TIMESTAMP]),
      kv[FINISHED] === 'true' ? true : false,
      yamPath,
      yaml.load(kv[yamPath]) as IApplicationModel,
      yaml.load(kv[PLUGIN_INFO_LIST] || '[]') as PluginInfoList
    )
  }

  toKV(): KVString {
    return {
      [APPLIED_RESOURCES]: yaml.dump(this.appliedResources),
      [SYNC_TIMESTAMP]: new Date().toISOString(),
      [FINISHED]: this.finished ? 'true' : 'false',
      [this.yamPath]: yaml.dump(this.appModel),
      [PLUGIN_INFO_LIST]: yaml.dump(this.pluginInfoList)
    }
  }

}
